import React from 'react';
import { PaperclipIcon, XIcon, FileTextIcon, FileIcon } from 'lucide-react';

// Ek Dosya Önizleme Kartı — hem Yeni Harcama hem Edit Modalında kullanılır.
// Görsel dosyalar için 60×60 thumbnail render eder, PDF için özel ikon,
// diğer türler için jenerik dosya ikonu. `attached_file` şu şekilde
// bekleniyor: {url, name, size, type}. `onRemove` opsiyonel — verilirse
// sağ üstte X butonu görünür.
const isImageFile = (file) => {
  const type = (file?.type || '').toLowerCase();
  const name = (file?.name || '').toLowerCase();
  return type.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(name);
};
const isPdfFile = (file) => {
  const type = (file?.type || '').toLowerCase();
  const name = (file?.name || '').toLowerCase();
  return type.includes('pdf') || /\.pdf$/i.test(name);
};

const AttachmentPreviewCard = ({ file, onRemove, testIdPrefix = 'attachment', showLink = true }) => {
  if (!file?.url) return null;
  const image = isImageFile(file);
  const pdf = isPdfFile(file);
  const sizeKb = file.size != null ? Math.round(file.size / 1024) : null;

  return (
    <div
      className="flex items-center justify-between gap-3 p-3 rounded-lg border border-teal-200 bg-teal-50"
      data-testid={`${testIdPrefix}-preview-card`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Thumbnail — resim ise gerçek önizleme, PDF ise kırmızı PDF ikonu,
            diğer türler için gri jenerik ikon. */}
        {image ? (
          <img
            src={file.url}
            alt={file.name || 'Belge önizleme'}
            className="w-14 h-14 object-cover rounded-md border border-teal-200 bg-white flex-shrink-0"
            onError={(e) => {
              // Yüklenemezse fallback ikon göster
              e.currentTarget.style.display = 'none';
              const fb = e.currentTarget.nextElementSibling;
              if (fb) fb.style.display = 'flex';
            }}
            data-testid={`${testIdPrefix}-thumbnail-image`}
          />
        ) : null}
        {(!image || true) && (
          <div
            className={`w-14 h-14 rounded-md border flex items-center justify-center flex-shrink-0 ${
              pdf ? 'border-red-200 bg-white' : 'border-gray-200 bg-white'
            }`}
            style={{ display: image ? 'none' : 'flex' }}
            data-testid={`${testIdPrefix}-thumbnail-icon`}
          >
            {pdf ? (
              <div className="flex flex-col items-center leading-none">
                <FileTextIcon className="w-6 h-6 text-red-500" />
                <span className="text-[9px] font-bold text-red-600 mt-0.5">PDF</span>
              </div>
            ) : (
              <FileIcon className="w-6 h-6 text-gray-400" />
            )}
          </div>
        )}

        {/* Meta */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <PaperclipIcon className="w-3.5 h-3.5 text-teal-700 flex-shrink-0" />
            {showLink ? (
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-teal-800 truncate hover:underline"
                title={file.name}
                data-testid={`${testIdPrefix}-name-link`}
              >
                {file.name || 'Belge'}
              </a>
            ) : (
              <span
                className="text-sm font-medium text-teal-800 truncate"
                title={file.name}
              >
                {file.name || 'Belge'}
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-teal-700">
            {sizeKb != null && <span>{sizeKb} KB</span>}
            {file.type && <span className="text-teal-500">·</span>}
            {file.type && (
              <span className="truncate">
                {file.type.split('/')[1]?.toUpperCase() || file.type}
              </span>
            )}
          </div>
        </div>
      </div>

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="w-7 h-7 rounded-md hover:bg-teal-100 flex items-center justify-center flex-shrink-0"
          aria-label="Belgeyi kaldır"
          title="Kaldır"
          data-testid={`${testIdPrefix}-remove-btn`}
        >
          <XIcon className="w-4 h-4 text-teal-700" />
        </button>
      )}
    </div>
  );
};

export default AttachmentPreviewCard;
